/* eslint-disable theme-colors/no-literal-colors */
import React, { useState, useEffect } from 'react';
import { styled, SupersetTheme } from '@superset-ui/core';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Tooltip } from 'src/components/Tooltip';
import { generateDefaultAvatar, getUserAvatarApiUrl } from 'src/utils/avatar';
import { ListCellVariant } from 'src/components/ListView/cellPro';

interface FacePileProps {
  users: {
    first_name: string;
    last_name: string;
    id: number;
    label?: string;
    value?: number;
  }[];
  maxCount?: number;
  variant?: ListCellVariant;
}

const customAvatarStyler = (theme: SupersetTheme) => `
  width: ${theme.gridUnit * 6}px;
  height: ${theme.gridUnit * 6}px;
  line-height: ${theme.gridUnit * 6}px;
  font-size: ${theme.typography.sizes.m}px;
  box-shadow: inset 1px 1px 1px ${
    theme.colors.grayscale.light2
  }, inset -0.5px -1px 1px ${theme.colors.grayscale.light4};
  vertical-align: middle;
`;

const StyledGroup = styled(Avatar.Group)<{ $variant?: ListCellVariant }>`
  .ant-avatar {
    ${({ theme, $variant }) => `
      ${customAvatarStyler(theme)}
      ${
        $variant === 'pro'
          ? `
        box-shadow: none;
        border: 2px solid ${theme.colors.grayscale.light5};
        margin-left: -8px;
      `
          : ''
      }
    `}
  }

  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  ${({ $variant }) =>
    $variant === 'pro'
      ? `
    .ant-avatar:first-child {
      margin-left: 0;
    }
  `
      : ''}
`;

// 单个用户头像组件 - 支持服务器头像加载和默认头像回退
function UserAvatarItem({
  user,
  size = 24,
}: {
  user: {
    first_name: string;
    last_name: string;
    id: number;
    label?: string;
    value?: number;
  };
  size?: number;
}) {
  const [avatarError, setAvatarError] = useState(false);
  const [avatarKey, setAvatarKey] = useState<number | undefined>(undefined);
  const { first_name, last_name, id, label } = user;

  // 如果 first_name 或 last_name 不存在，则从 label 中提取
  let displayNameFirst = first_name;
  let displayNameLast = last_name;

  if (!first_name || !last_name) {
    const [firstName, lastName] = (label || '').split(' ');
    displayNameFirst = firstName || '';
    displayNameLast = lastName || '';
  }

  const displayNameLabel =
    label || `${displayNameFirst} ${displayNameLast}`.trim();

  const name =
    `${displayNameFirst} ${displayNameLast}`.trim() || displayNameLabel;

  const defaultAvatar = generateDefaultAvatar({
    seed: String(id),
    size,
    style: 'shapes',
  });

  useEffect(() => {
    const handleAvatarUpdate = (
      e: CustomEvent<{ key: number; userId?: number }>,
    ) => {
      if (e.detail.userId !== id) {
        return;
      }
      setAvatarKey(e.detail.key);
      setAvatarError(false);
    };
    window.addEventListener(
      'avatarUpdated',
      handleAvatarUpdate as EventListener,
    );
    return () => {
      window.removeEventListener(
        'avatarUpdated',
        handleAvatarUpdate as EventListener,
      );
    };
  }, [id]);

  return (
    <Tooltip key={id} title={name} placement="top">
      <Avatar
        size={size}
        src={avatarError ? defaultAvatar : getUserAvatarApiUrl(id, avatarKey)}
        icon={<UserOutlined />}
        style={{ backgroundColor: '#e6f7ff' }}
        onError={() => {
          setAvatarError(true);
          return false;
        }}
      />
    </Tooltip>
  );
}

export default function FacePile({
  users,
  maxCount = 4,
  variant = 'default',
}: FacePileProps) {
  return (
    <StyledGroup maxCount={maxCount} $variant={variant}>
      {users.map(user => (
        <UserAvatarItem key={user.id} user={user} />
      ))}
    </StyledGroup>
  );
}
